import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import {
  useAdminAddPermissionToRole,
  useAdminGetRole,
  useAdminListRoles,
  useAdminRemovePermissionFromRole,
} from "@/services/api/admin/admin";
import { useState } from "react";
import { TableCard } from "@/components/application/table/table";
import { Button } from "@/components/base/buttons/button";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import type { RoleDto } from "@/types/api";

export const Route = createFileRoute("/admin/roles")({
  component: RolesPage,
});

function RolesPage() {
  const { data: roles, isLoading, error } = useAdminListRoles();
  const [selected, setSelected] = useState<string | null>(null);
  const [newPerm, setNewPerm] = useState("");

  const { data: roleDetails, refetch: refetchRole } = useAdminGetRole(selected ?? "", {
    query: { enabled: !!selected },
  });
  const addPerm = useAdminAddPermissionToRole();
  const removePerm = useAdminRemovePermissionFromRole();

  const onAdd = async () => {
    if (!selected || !newPerm.trim()) return;
    await addPerm.mutateAsync({ role: selected, data: { permission: newPerm.trim() } });
    setNewPerm("");
    await refetchRole();
  };

  const onRemove = async (perm: string) => {
    if (!selected) return;
    await removePerm.mutateAsync({ role: selected, permission: perm });
    await refetchRole();
  };

  const errorMessage = error instanceof Error ? error.message : undefined;
  const availableRoles = (roles ?? []).filter(
    (role): role is RoleDto & { roleName: string } =>
      typeof role.roleName === "string" && role.roleName.trim().length > 0,
  );
  const permissions = roleDetails?.permissions ?? [];

  return (
    <ProtectedRoute requiredPermissions={["Roles.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 p-6 md:grid-cols-3">
          <div className="md:col-span-1">
            <TableCard.Root>
              <TableCard.Header title="Roles" />
              {isLoading && <div className="mt-3">Loading…</div>}
              {errorMessage && (
                <div className="m-4 rounded border border-red-300 bg-red-50 p-3 text-red-800">
                  {errorMessage ?? "Failed to load roles"}
                </div>
              )}
              <ul className="divide-y">
                {availableRoles.map((role) => (
                  <li key={role.roleName}>
                    <button
                      className={`w-full px-4 py-3 text-left ${selected === role.roleName ? "bg-secondary" : ""}`}
                      onClick={() => setSelected(role.roleName)}
                    >
                      {role.roleName}
                    </button>
                  </li>
                ))}
              </ul>
            </TableCard.Root>
          </div>
          <div className="md:col-span-2">
            <TableCard.Root>
              <TableCard.Header
                title="Permissions"
                description={selected ? undefined : "Select a role to view permissions"}
              />
              {selected && (
                <div className="mt-3">
                  <div className="mb-3 flex gap-2">
                    <input
                      value={newPerm}
                      onChange={(e) => setNewPerm(e.target.value)}
                      placeholder="Permission (e.g., Users.Read)"
                      className="border-arc-base-black rounded-lg border-2 px-3 py-2"
                    />
                    <Button color="secondary" onClick={onAdd}>
                      Add
                    </Button>
                  </div>
                  <ul className="divide-y rounded border">
                    {permissions.map((permission) => (
                      <li key={permission} className="flex items-center justify-between px-3 py-2">
                        <span>{permission}</span>
                        <Button color="secondary" size="sm" onClick={() => onRemove(permission)}>
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </TableCard.Root>
          </div>
        </div>
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
